import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import Popover from "@govlaunch/popover";
import React, { ReactElement } from "react";
import { useToggle } from "react-use";
import isOptimisticId from "~/lib/utils/isOptimisticId";
import { IComment, IStory } from "~/types/types";
import ColoredThumbsUpIcon from "~/components/icons/ColoredThumbsUpIcon";
import ThumbsUpIcon from "~/components/icons/ThumbsUpIcon";
import { FormattedMessage, useIntl } from "react-intl";
import ReplyIcon from "~/components/icons/CommentReplyIcon";
import DotdotdotIcon from "~/components/icons/DotDotDotIcon";

interface ICommentActionsProps {
  comment: Pick<IComment, "_id" | "upvotesCount" | "viewerDidUpvote" | "author">;
  selfie: Pick<IStory, "_id">;
  onReply: () => void;
  onToggleUpvote: () => void;
  onEdit: () => void;
  onDelete: () => void;
  preview?: boolean;
}

export default function CommentActions({
  comment,
  selfie,
  onReply,
  onToggleUpvote,
  onEdit,
  onDelete,
  preview = false,
}: ICommentActionsProps): ReactElement {
  const [on, toggle] = useToggle(false);
  const intl = useIntl();

  return (
    <div
      css={{
        display: "flex",
        flexWrap: "wrap",
      }}
    >
      <Button
        theme="link"
        height="auto"
        style={{
          padding: 0,
          height: "auto",
          fontWeight: 500,
          fontSize: 12,
          textTransform: "uppercase",
        }}
        textColor={comment.viewerDidUpvote ? palette.primary : palette.sealBlue}
        type="button"
        onClick={onToggleUpvote}
        disabled={isOptimisticId(comment._id) || preview}
      >
        <span css={{ fontSize: 12 }}>
          {comment.viewerDidUpvote ? (
            <ColoredThumbsUpIcon height={14} width={14} />
          ) : (
            <ThumbsUpIcon height={14} width={14} />
          )}
        </span>
      </Button>
      <Button
        theme="link"
        style={{
          padding: 0,
          fontWeight: 500,
          textTransform: "uppercase",
          fontSize: 12,
          height: "auto",
          marginLeft: 10,
        }}
        textColor={palette.sealBlue}
        type="button"
        onClick={onReply}
        disabled={isOptimisticId(comment._id) || preview}
      >
        <span css={{ fontSize: 12 }}>
          <ReplyIcon />
        </span>

        <div
          css={{
            marginLeft: 5,
          }}
        >
          <FormattedMessage defaultMessage="Reply" id="9HU8vw" />
        </div>
      </Button>
      {comment.author && selfie._id === comment.author._id && (
        <Popover
          isOpen={on}
          onClickOutside={toggle}
          eventsEnabled={false}
          boxStyle={{
            zIndex: 9999,
            boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
          }}
          render={() => (
            <div css={{ padding: 15 }}>
              <ul
                css={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  "& > li:not(:last-child)": {
                    marginBottom: 10,
                  },
                }}
              >
                <li>
                  <Button
                    theme="link"
                    style={{
                      padding: 0,
                      height: "auto",
                      fontWeight: 500,
                    }}
                    textColor={palette.mediumGray}
                    disabled={isOptimisticId(comment._id)}
                    onClick={() => {
                      onEdit();
                      toggle();
                    }}
                  >
                    <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
                  </Button>
                </li>

                <li>
                  <Button
                    theme="link"
                    style={{
                      padding: 0,
                      height: "auto",
                      fontWeight: 500,
                    }}
                    textColor={palette.mediumGray}
                    disabled={isOptimisticId(comment._id)}
                    onClick={() => {
                      onDelete();
                      toggle();
                    }}
                  >
                    <FormattedMessage defaultMessage="Delete" id="K3r6DQ" />
                  </Button>
                </li>
              </ul>
            </div>
          )}
        >
          {({ innerRef }: { innerRef: React.Ref<HTMLSpanElement> }) => (
            <span
              ref={innerRef}
              onClick={toggle}
              role="button"
              aria-label={intl.formatMessage({
                defaultMessage: "Toggle Edit/Delete menu",
                id: "Z+U/eF",
              })}
              css={{
                margin: 0,
                padding: 0,
                border: "none",
                color: palette.sealBlue,
                display: "inline-flex",
                cursor: "pointer",
                marginLeft: 15,
              }}
            >
              <DotdotdotIcon />
            </span>
          )}
        </Popover>
      )}
    </div>
  );
}
